import type { ReactElement } from "react"
import React from "react"
import _t from "@core/i18n"
import { get } from "lodash"
import useCurrentPlanSettings from "@gatsby-plugin-marketplace/hooks/useCurrentPlanSettings"
import type { ParametersTableProps } from "~/components/ParametersTable"
import ParametersTable from "~/components/ParametersTable"
import useCurrentProducts from "~/components/ParametersTable/useCurrentProducts"
import { ParametersFooter } from "~/components/ProductParameters/ParametersFooter"
import type { ComparableProduct } from "~/fragments/comparableProduct"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import useCurrentPathname from "~/hooks/useCurrentPathname"
import useLinkPropertiesCollection from "~/hooks/useLinkPropertiesCollection"
import type { ComparableProductsPackage } from "~/types/squidex"

type MarketProductParametersProps = {
  saleConfiguration?: SaleConfiguration
  customHeaderLinkText?: string
  hasHiddenProductPrice?: boolean
  hasHiddenParametersName?: boolean
  hasHiddenFooter?: boolean
  products: ComparableProduct[]
  packages?: ComparableProductsPackage[]
} & Omit<
  ParametersTableProps,
  "generateHeaderLink" | "currentProducts" | "headerColumns" | "productId"
>

export default function MarketProductParameters({
  saleConfiguration,
  products,
  tableHeaderTitle,
  customHeaderLinkText,
  hasHiddenFooter,
  hasHiddenProductPrice,
  hasHiddenParametersName,
}: MarketProductParametersProps): ReactElement<MarketProductParametersProps> {
  const currentPlanSettings = useCurrentPlanSettings(saleConfiguration)
  const urls = useLinkPropertiesCollection(
    products
      .map((product): string | undefined =>
        get(product, "flatData.pages.0.flatData.url")
      )
      .filter((url): url is string => typeof url === "string")
  )

  const currentPathname = useCurrentPathname()

  const generateHeaderLink = (
    item: ComparableProduct,
    comparableProducts: ComparableProduct[]
  ) => {
    const itemSaleConfigurationId = get(
      item,
      "flatData.saleConfigurations[0].id"
    )
    const itemDefaultPlanAlias = get(
      item,
      "flatData.saleConfigurations[0].flatData.defaultCloudBluePlan[0].flatData.alias"
    )

    const currentPlanSettingsPlanAlias = get(
      currentPlanSettings,
      "cloudBluePlan[0].flatData.alias"
    )

    const isSaleConfigActive =
      saleConfiguration?.id && saleConfiguration?.id === itemSaleConfigurationId

    const isDefaultPlanActive =
      itemSaleConfigurationId &&
      itemDefaultPlanAlias === currentPlanSettingsPlanAlias

    const isActive = isSaleConfigActive || isDefaultPlanActive

    const url = get(item, "flatData.pages.0.flatData.url")
    const linkProps = url && currentPathname !== url ? urls[url] : null

    return {
      title: item.flatData.name,
      isActive: comparableProducts.length > 1 ? isActive : false,
      link: linkProps
        ? {
            text:
              customHeaderLinkText || _t("marketplace.checkProductDescription"),
            ...linkProps,
          }
        : undefined,
    }
  }

  const [currentProducts, headerColumns] = useCurrentProducts(products)

  return (
    <ParametersTable
      products={currentProducts}
      headerColumns={headerColumns}
      saleConfiguration={saleConfiguration}
      tableHeaderTitle={tableHeaderTitle}
      generateHeaderLink={generateHeaderLink}
      footer={
        hasHiddenFooter
          ? undefined
          : currentProducts.map((item) => {
              const hasPlan = !!get(
                item,
                "flatData.saleConfigurations[0].flatData.defaultCloudBluePlan[0].id"
              )

              if (!hasPlan) {
                return undefined
              }

              return (
                <ParametersFooter
                  key={item.id}
                  item={item}
                  productId={saleConfiguration?.id}
                />
              )
            })
      }
      hasHiddenProductPrice={
        hasHiddenProductPrice ? hasHiddenProductPrice : undefined
      }
      hasHiddenParametersName={
        hasHiddenParametersName ? hasHiddenParametersName : undefined
      }
    />
  )
}
